import { useCallback, useEffect, useState } from 'react';

import { fitnessQuestionnaireService } from '@personal-trainer-app/shared';

import type { SavedQuestionnaireAnswers } from '@personal-trainer-app/shared/src/utils/questionnaire/questionnaire-form';

export type { SavedQuestionnaireAnswers };

export interface UseSavedQuestionnaireReturn {
  /** The saved answers, or `null` when none exist yet / not loaded. */
  data: SavedQuestionnaireAnswers | null;
  isLoading: boolean;
  /** True once the fetch for the current `enabled` session has resolved. */
  hasFetched: boolean;
  error: string | null;
  /** Load the answers again — after a failed load. */
  refetch: () => void;
}

/**
 * Loads the user's latest saved questionnaire answers (zod-ready shape) for
 * pre-filling the refine modal and the sign-up questionnaire. A `404` means the user has never submitted —
 * that is "start fresh", not an error, so it resolves to `data: null`.
 *
 * Only fetches while `enabled` (typically: the modal is open), refetching each
 * time it flips back on so a re-opened modal reflects the latest saved state.
 */
export function useSavedQuestionnaire(
  enabled: boolean
): UseSavedQuestionnaireReturn {
  const [data, setData] = useState<SavedQuestionnaireAnswers | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchToken, setRefetchToken] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setHasFetched(false);
    setError(null);

    void (async () => {
      try {
        // `null` when nothing was ever saved: start fresh.
        const saved = await fitnessQuestionnaireService.getSaved();
        if (cancelled) return;
        setData(saved);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error
            ? err.message
            : 'Could not load your saved answers.'
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setHasFetched(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, refetchToken]);

  const refetch = useCallback(() => setRefetchToken((token) => token + 1), []);

  return { data, isLoading, hasFetched, error, refetch };
}

export default useSavedQuestionnaire;
